import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, switchMap, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CartService, CartDialogService } from '@app/core/cart/services';
import { HttpErrorResponse } from '@angular/common/http';

import {
  AddToCart,
  AddToCartSuccess,
  AddToCartFailure,
  LoadCartSuccess,
  LoadCartFailure,
  RemoveFromCart,
  RemoveFromCartSuccess,
  RemoveFromCartFailure,
  CartActionTypes,
  LoadCart
} from '../actions/cart.actions';

import * as headerActions from '@app/core/header/store/actions/header.action';
import * as reservationActions from '@app/+product/store/actions/reservation.action';
import { PriceDropService } from '@app/shared/services/price-drop/priceDrop.service';

@Injectable()
export class CartEffects {
  @Effect()
  loadCart$ = this.actions$.pipe(
    ofType(CartActionTypes.LoadCart),
    switchMap(action => {
      return this.cartService.getCart().pipe(
        map(order => new LoadCartSuccess({ order })),
        catchError(error => of(new LoadCartFailure(error)))
      );
    })
  );

  @Effect()
  loadCartSuccess$ = this.actions$.pipe(
    ofType(CartActionTypes.LoadCartSuccess),
    map((action: LoadCartSuccess) => action.payload),
    map(({ order }) => new headerActions.SetCountersnumbers({ orderLineCount: order?.orderLines?.length || 0 }))
  );

  @Effect()
  addToCart$ = this.actions$.pipe(
    ofType(CartActionTypes.AddToCart),
    map((action: AddToCart) => action.payload),
    switchMap(action => {
      return this.cartService.addToCart(action.product).pipe(
        map(
          orderLine =>
            new AddToCartSuccess({
              product: action.product,
              quantity: action.quantity || 1,
              orderLine,
              openDialog: action.openDialog
            })
        ),
        catchError((err: HttpErrorResponse) => of(new AddToCartFailure(err.error.errors)))
      );
    })
  );

  @Effect()
  addToCartSuccess$ = this.actions$.pipe(
    ofType(CartActionTypes.AddToCartSuccess),
    map((action: AddToCartSuccess) => action.payload),
    switchMap(payload => {
      if (payload.openDialog) {
        this.cartDialogService.confirmAddToCart(payload.product);
      }
      this.priceDropService.addPriceDropToLocalForage(payload.product.id);

      return [
        new headerActions.IncrementCountersCart(),
        new reservationActions.CheckReservation({ productId: payload.product.id }),
        new LoadCart()
      ];
    })
  );

  @Effect({ dispatch: false })
  addToCartFailure$ = this.actions$
    .pipe(
      ofType(CartActionTypes.AddToCartFailure),
      map((action: AddToCartFailure) => action.payload)
    )
    .pipe(
      tap(error => {
        this.cartDialogService.addToCartError(error);
      })
    );

  @Effect()
  removeFromCart$ = this.actions$.pipe(
    ofType(CartActionTypes.RemoveFromCart),
    map((action: RemoveFromCart) => action.payload),
    switchMap(item => {
      return this.cartService.removeFromCart(item).pipe(
        map(res => new RemoveFromCartSuccess(item)),
        catchError(error => {
          // Product is not in cart anymore
          if (error?.status === 400) {
            return of(new RemoveFromCartSuccess(item));
          }
          return of(new RemoveFromCartFailure());
        })
      );
    })
  );

  @Effect()
  RemoveFromCartSuccess$ = this.actions$.pipe(
    ofType(CartActionTypes.RemoveFromCartSuccess),
    map((action: RemoveFromCartSuccess) => action.payload),
    switchMap(item => [
      new headerActions.DecrementCountersCart(),
      new reservationActions.CancelReservation({ productId: item.product.id }),
      new LoadCart()
    ])
  );

  constructor(
    private actions$: Actions,
    private cartService: CartService,
    private cartDialogService: CartDialogService,
    private priceDropService: PriceDropService
  ) {}
}
